<template>
  <div class="splash-page" :class="{ 'splash-once': !loop }">
    <svg
      class="splash-svg"
      viewBox="0 0 1280 720"
      role="img"
      aria-label="Virtual Vertex LabStream cinematic splash animation"
    >
      <defs>
        <radialGradient id="bgGlow" cx="52%" cy="48%" r="60%">
          <stop offset="0%" stop-color="#d9ecfb" stop-opacity="0.95" />
          <stop offset="58%" stop-color="#eef5fb" stop-opacity="0.72" />
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
        </radialGradient>

        <linearGradient id="vvBlue" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#2fc3ff" />
          <stop offset="45%" stop-color="#0f8fd6" />
          <stop offset="100%" stop-color="#0a5f9d" />
        </linearGradient>

        <linearGradient id="vvLeftPiece" x1="0%" y1="0%" x2="70%" y2="100%">
          <stop offset="0%" stop-color="#35b4ea" />
          <stop offset="60%" stop-color="#2498cf" />
          <stop offset="100%" stop-color="#1e88bc" />
        </linearGradient>

        <linearGradient id="vvRightPiece" x1="0%" y1="0%" x2="60%" y2="100%">
          <stop offset="0%" stop-color="#1a86c8" />
          <stop offset="55%" stop-color="#0f74b4" />
          <stop offset="100%" stop-color="#0b5f97" />
        </linearGradient>

        <linearGradient id="lightSweep" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0" />
          <stop offset="50%" stop-color="#87cdf6" stop-opacity="0.72" />
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
        </linearGradient>

        <filter id="neon" x="-150%" y="-150%" width="400%" height="400%">
          <feGaussianBlur stdDeviation="7" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

      </defs>

      <rect width="1280" height="720" fill="#ffffff" />
      <rect width="1280" height="720" fill="url(#bgGlow)" />

      <g class="stars" aria-hidden="true">
        <circle cx="130" cy="130" r="2" />
        <circle cx="280" cy="540" r="1.5" />
        <circle cx="430" cy="160" r="1.6" />
        <circle cx="590" cy="600" r="1.2" />
        <circle cx="810" cy="120" r="1.4" />
        <circle cx="960" cy="620" r="1.8" />
        <circle cx="1140" cy="210" r="1.3" />
      </g>

      <g class="brand-lockup" transform="translate(270 346)">
        <g class="ring-block">
        <circle
          class="ring-base"
          cx="0"
          cy="0"
          r="132"
          fill="none"
          stroke="#32aeea"
          stroke-width="6"
          stroke-opacity="0.42"
        />

        <circle
          class="ring"
          cx="0"
          cy="0"
          r="132"
          fill="none"
          stroke="url(#vvBlue)"
          stroke-width="6"
          filter="url(#neon)"
        />

        <g class="vv-mark" filter="url(#neon)">
          <g class="vv-ghost" aria-hidden="true">
            <polygon class="vv-ghost-right" points="30,-78 68,-78 24,76 -8,76" />
            <polygon class="vv-ghost-left" points="-68,-78 -30,-78 8,76 -24,76" />
            <polygon class="vv-ghost-dent" points="-12,-76 12,-76 0,-42" />
          </g>
          <polygon class="vv-right-piece" points="30,-78 68,-78 24,76 -8,76" />
          <polygon class="vv-left-piece" points="-68,-78 -30,-78 8,76 -24,76" />
          <polygon class="vv-dent-piece" points="-12,-76 12,-76 0,-42" />
        </g>
        </g>

        <g class="wordmark">
          <text x="250" y="0" class="lab-text">Lab</text>
          <line class="word-separator" x1="468" y1="-52" x2="468" y2="52" />
          <text x="500" y="0" class="stream-text">Stream</text>
        </g>
      </g>

      <text x="520" y="468" class="final-copy">
        Precision workflows for modern laboratories
      </text>

      <rect
        class="beam"
        x="140"
        y="105"
        width="280"
        height="500"
        fill="url(#lightSweep)"
        transform="rotate(8 280 360)"
      />

      <g class="scanline" aria-hidden="true">
        <line x1="488" y1="498" x2="1178" y2="498" />
      </g>
    </svg>

    <div class="bottom-deck">
      <div class="deck-brand">
        <div class="deck-title">LabStream by Virtual Vertex s.r.l.</div>
      </div>
      <div class="deck-meta">
        <span>v{{ appVersion }}</span>
        <span>EU Region</span>
        <span>2021-{{ currentYear }} Virtual Vertex s.r.l.</span>
      </div>
    </div>
  </div>
</template>

<script>
const packageInfo = require("../../../package.json");

export default {
  name: "SplashShowcase",
  props: {
    loop: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      appVersion: packageInfo.version,
      currentYear: new Date().getFullYear(),
    };
  },
};
</script>

<style scoped>
.splash-page {
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: radial-gradient(circle at 16% 8%, #f2f8fe 0%, #ffffff 60%, #f7fbff 100%);
}

.splash-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.splash-once .ring,
.splash-once .vv-left-piece,
.splash-once .vv-right-piece,
.splash-once .vv-dent-piece,
.splash-once .beam,
.splash-once .wordmark,
.splash-once .final-copy,
.splash-once .scanline line,
.splash-once .stars circle {
  animation-iteration-count: 1 !important;
  animation-fill-mode: forwards !important;
}

.stars circle {
  fill: #6fb5e2;
  opacity: 0;
  animation: starPulse 3.8s ease-in-out infinite;
}

.stars circle:nth-child(2) {
  animation-delay: 0.4s;
}

.stars circle:nth-child(3) {
  animation-delay: 1.1s;
}

.stars circle:nth-child(4) {
  animation-delay: 1.7s;
}

.stars circle:nth-child(5) {
  animation-delay: 2.3s;
}

.stars circle:nth-child(6) {
  animation-delay: 0.8s;
}

.stars circle:nth-child(7) {
  animation-delay: 2.9s;
}

.ring {
  stroke-dasharray: 829;
  stroke-dashoffset: 829;
  animation: ringDraw 4.2s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

.ring-base {
  opacity: 1;
}

.vv-mark {
  opacity: 1;
}

.vv-left-piece {
  fill: url(#vvLeftPiece);
  opacity: 0;
  transform-origin: -20px 8px;
  animation: vvLeftIn 4.2s cubic-bezier(0.2, 0.8, 0.2, 1) infinite;
}

.vv-ghost-left {
  fill: #2498cf;
  opacity: 0.2;
}

.vv-ghost-right {
  fill: #0f74b4;
  opacity: 0.17;
}

.vv-ghost-dent {
  fill: #2ca8de;
  opacity: 0.2;
}

.vv-right-piece {
  fill: url(#vvRightPiece);
  opacity: 0;
  transform-origin: 20px 8px;
  animation: vvRightIn 4.2s cubic-bezier(0.2, 0.8, 0.2, 1) infinite;
}

.vv-dent-piece {
  fill: #2ca8de;
  opacity: 0;
  transform-origin: 0px -58px;
  animation: vvDentIn 4.2s ease-in-out infinite;
}

.beam {
  opacity: 0;
  animation: beamSweep 4.2s ease-in-out infinite;
}

.lab-text,
.stream-text {
  font-family: "Montserrat", "Helvetica Neue", Arial, sans-serif;
  font-size: 96px;
  letter-spacing: 1px;
  dominant-baseline: middle;
}

.lab-text {
  fill: #2ab6f2;
  font-weight: 700;
}

.stream-text {
  fill: #5d6570;
  font-weight: 300;
}

.word-separator {
  stroke: #5d6570;
  stroke-width: 3;
  opacity: 0.8;
}

.wordmark {
  opacity: 0.72;
  animation: wordFade 4.2s ease-in-out infinite;
}

.final-copy {
  fill: #56748e;
  font-size: 24px;
  letter-spacing: 1px;
  font-family: "Montserrat", "Helvetica Neue", Arial, sans-serif;
  font-weight: 500;
  opacity: 0;
  animation: finalCopyReveal 4.2s ease-in-out infinite;
}

.scanline line {
  stroke: #45afea;
  stroke-width: 2;
  opacity: 0;
  stroke-dasharray: 690;
  stroke-dashoffset: 690;
  animation: scanlineDraw 4.2s ease-in-out infinite;
}

.bottom-deck {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 22px;
  padding: 16px 20px;
  border-radius: 16px;
  border: 1px solid rgba(123, 163, 194, 0.26);
  background: linear-gradient(115deg, rgba(255, 255, 255, 0.66), rgba(232, 243, 251, 0.58));
  backdrop-filter: blur(6px);
  color: #5d738b;
  font-family: "Montserrat", "Helvetica Neue", Arial, sans-serif;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.deck-brand {
  min-width: 290px;
  flex: 1;
}

.deck-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.6px;
}

.deck-meta {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  text-align: right;
  font-size: 11px;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  color: #4b6a84;
}

@keyframes ringDraw {
  0% {
    stroke-dashoffset: 829;
    opacity: 0.35;
    transform: scale(0.9);
  }
  14% {
    opacity: 1;
  }
  40% {
    stroke-dashoffset: 0;
    opacity: 1;
    transform: scale(1);
  }
  78% {
    opacity: 1;
  }
  100% {
    stroke-dashoffset: 0;
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes vvLeftIn {
  0%,
  4% {
    opacity: 0;
    transform: translate(-26px, 16px) scale(0.9);
  }
  10% {
    opacity: 0.85;
  }
  16% {
    transform: translate(3px, -3px) scale(1.01);
  }
  22%,
  100% {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
}

@keyframes vvRightIn {
  0%,
  6% {
    opacity: 0;
    transform: translate(26px, 16px) scale(0.9);
  }
  12% {
    opacity: 0.8;
  }
  18% {
    transform: translate(-3px, -3px) scale(1.01);
  }
  24%,
  100% {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
}

@keyframes vvDentIn {
  0%,
  8% {
    opacity: 0;
    transform: translateY(-16px);
  }
  14% {
    opacity: 0.9;
  }
  20% {
    transform: translateY(3px);
  }
  26%,
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes finalCopyReveal {
  0%,
  42% {
    opacity: 0;
    transform: translateY(8px);
  }
  62%,
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes beamSweep {
  0%,
  28% {
    opacity: 0;
    transform: translateX(-280px) rotate(8deg);
  }
  35% {
    opacity: 0.65;
  }
  52% {
    opacity: 0;
    transform: translateX(820px) rotate(8deg);
  }
  100% {
    opacity: 0;
    transform: translateX(820px) rotate(8deg);
  }
}

@keyframes wordFade {
  0%,
  33% {
    opacity: 0.78;
  }
  56%,
  100% {
    opacity: 1;
  }
}

@keyframes scanlineDraw {
  0%,
  50% {
    opacity: 0;
    stroke-dashoffset: 690;
  }
  64% {
    opacity: 0.82;
  }
  78%,
  100% {
    opacity: 0.72;
    stroke-dashoffset: 0;
  }
}

@keyframes starPulse {
  0%,
  100% {
    opacity: 0.08;
  }
  50% {
    opacity: 0.85;
  }
}

@media (max-width: 900px) {
  .bottom-deck {
    left: 12px;
    right: 12px;
    bottom: 10px;
    padding: 12px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .deck-brand {
    min-width: auto;
  }

  .deck-title {
    font-size: 10px;
  }

  .deck-subtitle,
  .deck-meta {
    font-size: 9px;
  }

  .deck-meta {
    text-align: left;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ring,
  .vv-left-piece,
  .vv-right-piece,
  .vv-dent-piece,
  .beam,
  .wordmark,
  .final-copy,
  .scanline line,
  .stars circle {
    animation: none !important;
  }

  .ring {
    stroke-dashoffset: 0;
    opacity: 1;
  }

  .vv-mark path,
  .vv-mark polygon,
  .wordmark,
  .scanline line {
    opacity: 1;
  }

}
</style>
